/* eslint-disable camelcase */
import { OptionType, SORT_ORDER, TableSortOption } from '@evinced-private/ui-common';

import { MOBILE_APP_ID_FILTER_ID } from 'src/apps/dashboard/constants/MobileDashboard.constants';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import {
	ConditionInt,
	IssuesSortFields,
	IssuesSortOrder,
	IssuesTextSearchFields
} from 'src/common/types/graphql.generated';

import { MobileDashboardAllIssuesQueryVariables } from '../MobileAllIssues.generated';

import {
	buildMobileBuCustomFieldsFilter,
	getMobileAppIdsFromFilters
} from './mobileFilterVariableBuilder';

const mobileAllIssuesSortFieldsMap: Record<string, IssuesSortFields> = {
	issueType: IssuesSortFields.TypeName,
	severity: IssuesSortFields.SeverityName,
	appId: IssuesSortFields.AppId,
	elementDescription: IssuesSortFields.ScanName,
	foundBy: IssuesSortFields.ProductName,
	sessionOrTest: IssuesSortFields.SessionName
};

const mobileAllIssuesSortOrderMap: Record<SORT_ORDER, IssuesSortOrder> = {
	[SORT_ORDER.ASC]: IssuesSortOrder.Ascending,
	[SORT_ORDER.DESC]: IssuesSortOrder.Descending
};

const buildDaysFilter = (dateRangeOptions?: OptionType[]): ConditionInt | undefined => {
	if (!dateRangeOptions?.length) {
		return { value: [30] };
	}

	const days = Number(dateRangeOptions[0].value);
	return Number.isNaN(days) ? { value: [30] } : { value: [days] };
};

export const buildMobileAllIssuesVariables = (
	filters: IFilterOptions,
	page: number,
	sizePerPage: number,
	sort?: TableSortOption,
	mobileBuIds?: string[]
): MobileDashboardAllIssuesQueryVariables => {
	const variablesBuilder = new QueryVariablesBuilder<MobileDashboardAllIssuesQueryVariables>({
		sortFieldsMap: mobileAllIssuesSortFieldsMap,
		sortOrderMap: mobileAllIssuesSortOrderMap
	});

	variablesBuilder.addPaginationParams(page, sizePerPage);

	const defaultSort: TableSortOption = {
		dataField: 'issueType',
		order: SORT_ORDER.DESC
	};
	variablesBuilder.addSortParam(sort || defaultSort);

	const daysFilter = buildDaysFilter(filters.dateRange);
	if (daysFilter) {
		variablesBuilder.addConditionParam('daysFilter', daysFilter.value);
	}

	variablesBuilder
		.addFilterParam('severityFilter', filters.severities)
		.addFilterParam('issueTypeFilter', filters.types);

	const appIds = getMobileAppIdsFromFilters(filters);
	if (appIds.length) {
		variablesBuilder.addFilterParam('appIdFilter', filters[MOBILE_APP_ID_FILTER_ID]);
	}

	const variables = variablesBuilder.getVariables();

	variables.customFieldsFilter = buildMobileBuCustomFieldsFilter(mobileBuIds);

	if (filters.searchValue) {
		const searchValue = filters.searchValue.toString().trim();
		if (searchValue) {
			variables.textSearch = {
				searchFields: [
					IssuesTextSearchFields.TypeName,
					IssuesTextSearchFields.AppId,
					IssuesTextSearchFields.ScanName,
					IssuesTextSearchFields.SessionName
				],
				searchValues: [searchValue],
				insensitive: true
			};
		}
	}

	return variables;
};
